import React, { useEffect, useState } from "react";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Tooltip from "@mui/material/Tooltip";
import styles from "../Study_Progress/DashboardUserCalender.module.scss";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../Redux/Store/Hooks";
import { dashboardCalenderData } from "../../../Redux/Slices/DashboardData/DashboardCalenderData";
import { ReactComponent as StreakLegend } from "../../../Assets/Images/Achievements/Streak_legend.svg";
import { Zap } from "lucide-react";

interface DayActivity {
  date: string;
  exam_count: number;
  test_count: number;
  questions_attempted: number;
  day?: number;
}

interface StudyStreakCardProps {
  Dailygoal: any;
}

const DashboardUserCalender: React.FC<StudyStreakCardProps> = ({
  Dailygoal,
}) => {
  const isUCAT =
    window.location.hostname.split(".")[0]?.includes("ucat") ||
    window.location.hostname.split(".")[0] === "192";
  const [currentDate, setCurrentDate] = useState(new Date());
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(dashboardCalenderData());
  }, [dispatch]);

  // Get Course Details from Redux
  const { data: DashboardCalenderData } = useAppSelector(
    (state) => state.DashboardCalenderDataSliceReducer
  );
  const mockActivityData: DayActivity[] = Array.isArray(
    DashboardCalenderData?.data
  )
    ? (DashboardCalenderData?.data as DayActivity[])
    : [];

  // 🎯 Highlight specific special dates
  const isGateExamDate = (dateString: string) => dateString === "2026-03-14";

  const getClass = (
    questionsCompleted: number,
    testsCompleted: number,
    examsCompleted: number,
    dateString: string
  ): string => {
    if (isGateExamDate(dateString)) return styles["calendar__day--gate"];

    const nonZeroCount = [
      questionsCompleted,
      testsCompleted,
      examsCompleted,
    ].filter((val) => val > 0).length;

    if (nonZeroCount === 0) return styles["calendar__day--level0"]; // empty
    if (nonZeroCount === 1) return styles["calendar__day--level1"]; // low
    if (nonZeroCount === 2) return styles["calendar__day--level2"]; // mid
    if (nonZeroCount === 3) return styles["calendar__day--level3"]; // high

    return styles["calendar__day--level0"];
  };

  const isToday = (dateString: string) =>
    dateString === new Date().toISOString().split("T")[0];

  function getDaysInMonth(
    year: number,
    month: number,
    activities: DayActivity[]
  ) {
    const daysArray: DayActivity[] = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = (firstDay.getDay() + 6) % 7; // Monday-start shift

    // Add blanks before first day
    for (let i = 0; i < startOffset; i++) {
      daysArray.push({
        date: "",
        day: undefined,
        exam_count: 0,
        test_count: 0,
        questions_attempted: 0,
      });
    }

    // Add each day of month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateString = `${year}-${String(month + 1).padStart(
        2,
        "0"
      )}-${String(d).padStart(2, "0")}`;
      const activity = activities.find((a) => a.date === dateString);
      daysArray.push(
        activity
          ? { ...activity, day: d }
          : {
            date: dateString,
            day: d,
            questions_attempted: 0,
            test_count: 0,
            exam_count: 0,
          }
      );
    }

    // ✅ Ensure grid fills only until last day (no trailing boxes)
    const totalCells = Math.ceil(daysArray.length / 7) * 7;
    while (daysArray.length < totalCells) {
      daysArray.push({
        date: "",
        day: undefined,
        exam_count: 0,
        test_count: 0,
        questions_attempted: 0,
      });
    }

    return daysArray;
  }

  const days = getDaysInMonth(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    mockActivityData
  );

  const weekDays = ["M", "T", "W", "T", "F", "S", "S"];

  return (
    <div className={styles.calendar}>
      <div className={styles.calendar__header}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          {currentDate.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })}
        </Typography>

        <div>
          <IconButton
            size="small"
            onClick={() =>
              setCurrentDate((d) => new Date(d.setMonth(d.getMonth() - 1)))
            }
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() =>
              setCurrentDate((d) => new Date(d.setMonth(d.getMonth() + 1)))
            }
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Weekdays */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "8px",
          // marginRight: "5px",
          justifyItems: "center",
        }}
      >
        {weekDays.map((d) => (
          <Typography variant="caption" align="center" key={d}>
            {d}
          </Typography>
        ))}
      </div>

      {/* Calendar Days */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: "8px",
          justifyItems: "center",
        }}
      >
        {days.map((dayData, idx) => (
          <div key={idx}>
            {dayData.day ? ( // ✅ Only render box if day exists
              <Tooltip
                title={
                  isGateExamDate(dayData.date) ? (
                    <div
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                        color: "green",
                      }}
                    >
                      {isUCAT ? "UCAT" : "GATE"} Exam 2026
                    </div>
                  ) : (
                    <div style={{ fontSize: "0.875rem" }}>
                      {dayData.questions_attempted} Questions
                      <br />
                      {dayData.test_count} Tests
                      <br />
                      {dayData.exam_count} Exams
                    </div>
                  )
                }
                arrow
              >
                <div
                  className={`${styles.calendar__day} ${getClass(
                    dayData.questions_attempted,
                    dayData.test_count,
                    dayData.exam_count,
                    dayData.date
                  )} ${isToday(dayData.date) ? styles["calendar__day--today"] : ""
                    }`}
                >
                  {dayData.day}
                </div>
              </Tooltip>
            ) : (
              <div className={styles["calendar__day--transparent"]} /> // ✅ Transparent empty cell
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className={styles.calendar__legend}>
        <span></span>
        <div style={{ display: "flex", gap: "0.25rem" }}>
          <div
            className={`${styles["calendar__legend-box"]} ${styles["calendar__legend-empty"]}`}
          />
          <div
            className={`${styles["calendar__legend-box"]} ${styles["calendar__legend-low"]}`}
          />
          <div
            className={`${styles["calendar__legend-box"]} ${styles["calendar__legend-mid"]}`}
          />
          <div
            className={`${styles["calendar__legend-box"]} ${styles["calendar__legend-high"]}`}
          />
        </div>
        <span></span>
      </div>

      <Divider variant="middle" sx={{ margin: "15px 0px" }} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: "20px",
        }}
      >
        <Box
          sx={{
            mt: 3,
            bgcolor: "#f9fafb",
            width: "100%",
            borderRadius: "10px",
            p: "12px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mt: 0.5,
              color: "#009DFF",
              fontSize: "16px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <StreakLegend
              style={{
                stroke: "#009DFF",
                strokeWidth: 1.0,
                color: "#009DFF",
                fill: "transparent",
              }}
            />
            {Dailygoal?.current_streak || 0} Days
          </Typography>

          <Typography sx={{ fontSize: "14px", color: "#b5b6b6ff" }}>
            Current Streak
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 3,
            bgcolor: "#f9fafb",
            width: "100%",
            borderRadius: "10px",
            p: "12px",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mt: 0.5,
              color: "Black",
              fontSize: "16px",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Zap
              style={{
                width: "20px",
                height: "20px",
                color: "black",
                fill: "transparent",
              }}
            />
            {Dailygoal?.longest_streak || 0} Days
          </Typography>

          <Typography sx={{ fontSize: "14px", color: "#b5b6b6ff" }}>
            Longest Streak
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

export default DashboardUserCalender;
